import React, {
    useCallback, useEffect, useMemo, useContext
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { readViewModelValue } from '@xengage/gw-jutro-adapters-react';
import { useValidation } from '@xengage/gw-portals-validation-react'
import { ViewModelServiceContext, ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import classNames from 'classnames';
import { wizardProps } from '@xengage/gw-portals-wizard-react';
import { withRouter } from 'react-router-dom';
import { Link } from '@jutro/router';
import { InputField, DropdownSelectField, InputNumberField, Button, Icon } from '@jutro/legacy/components';
import { accessibilityAddTableRow, focusNextElement, screenReaderAnnounce } from 'premier-capability-hooks';
import styles from './AuxiliaryMotorsComponent.module.scss';
import metadata from './AuxiliaryMotorsComponent.metadata.json5';
import messages from './AuxiliaryMotorsComponent.messages';
import { TranslatorContext } from '@jutro/legacy/locale';

function AuxiliaryMotorsComponent(props) {

    const {
        data: AuxiliaryMotorsVM,
        updateWizardData,
        submissionVM,
        AuxiliaryMotorsPath,
        boatInformationPath,
        id: auxiliaryMotorId
    } = props;
    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);

    const addAuxiliaryMotors = useCallback(
        (event) => {
            if (event) {
                event.preventDefault();
            }

            AuxiliaryMotorsVM.value = AuxiliaryMotorsVM.value || []

            const { _xCenter, _dtoName } = AuxiliaryMotorsVM;

            const AuxiliaryMotorsObjVM = viewModelService.create(
                {},
                _xCenter,
                _dtoName
            );

            AuxiliaryMotorsObjVM.isValidationReq = true;
            AuxiliaryMotorsObjVM.yeaFie = (_.get(submissionVM, boatInformationPath))?.value?.yeaFie;
            AuxiliaryMotorsObjVM.motTypFie = { code: "Gas", name: "Gas" }

            AuxiliaryMotorsVM.pushElement(
                AuxiliaryMotorsObjVM
            );
            updateWizardData(submissionVM);
            accessibilityAddTableRow('Auxiliary Motors');
            setTimeout(() => {
                focusNextElement(true, 0, `${auxiliaryMotorId}yeaFie`);
            }, 3000);
             
        },
        [AuxiliaryMotorsVM, viewModelService, submissionVM, boatInformationPath, updateWizardData]
    );

    const handleValueChange = useCallback(
        () => {
            updateWizardData(submissionVM);
        },
        [submissionVM, updateWizardData]
    );

    const removeAssignmentItem = useCallback(
        (event, index) => {
            if (event) {
                event.preventDefault();
            }

            const removedAuxiliaryMotor = AuxiliaryMotorsVM.value[index];

            AuxiliaryMotorsVM.value.splice(index, 1);
            _.set(submissionVM.value, AuxiliaryMotorsPath, AuxiliaryMotorsVM.value);
            updateWizardData(submissionVM);
            screenReaderAnnounce(translator(messages.removeAuxMotor, {brandDesc: removedAuxiliaryMotor.braFie}))
            focusNextElement(true, 0, `addAuxiliaryMotorsButtonId`); 
        }, [submissionVM, updateWizardData, AuxiliaryMotorsPath, AuxiliaryMotorsVM?.value]
    );

    const getTableCell = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-shadow
        (item, index, props) => {
            const { path } = props;

            if (path === 'numFie') {
                const tempAuxiliaryMotorsPath = 'lobData.recMarLine.coverables.boats[0].additionalParts.auxiliaryMotorTable'
                const auxiliaryArray = _.get(submissionVM.value, tempAuxiliaryMotorsPath);
                const newNumber = auxiliaryArray?.length >= 2 ? auxiliaryArray[auxiliaryArray.length - 2].numFie + 1 : index + 1;

                if (auxiliaryArray[index].numFie === undefined) {
                    auxiliaryArray[index].numFie = newNumber;
                    _.set(submissionVM.value, tempAuxiliaryMotorsPath, AuxiliaryMotorsVM.value);
                }

                const value = item.numFie ? item.numFie : newNumber;

                return (
                    <InputField
                        id={index + path}
                        className={styles.tableColumn}
                        path={path}
                        dataType="number"
                        value={value}
                        readOnly
                    />
                );
            }

            if (path === 'yeaFie') {
                const value = item[path] ? item[path] : (_.get(submissionVM, boatInformationPath))?.value?.yeaFie;

                return (
                    <InputField
                        id={index + auxiliaryMotorId+ path}
                        className={styles.tableColumn}
                        path={path}
                        dataType="String"
                        onValueChange={(val, changedPath) => {
                            const filteredVal = String(val).replace(/[^0-9]/g, ''); // keep only digits

                            item.yeaFie = filteredVal;
                            handleValueChange(filteredVal, changedPath, index);
                        }}
                        value={value}
                        maxLength="4"
                    />
                );
            }

            if (path === 'braFie') {
                const value = item.braFie ? item.braFie : '';

                return (
                    <InputField
                        id={index + path}
                        className={styles.tableColumn}
                        path={path}
                        dataType="String"
                        onValueChange={(val, changedPath) => {
                            item.braFie = val;
                            handleValueChange(val, changedPath, index);
                        }}
                        value={value}
                    />
                );
            }

            if (path === 'motTypFie') {
                // const value = (item && item.motTypFie) ? item.motTypFie : undefined;
                // eslint-disable-next-line security/detect-object-injection
                const availableValues = AuxiliaryMotorsVM.length > 0 && AuxiliaryMotorsVM.children[index].motTypFie
                    // eslint-disable-next-line security/detect-object-injection
                    ? AuxiliaryMotorsVM.children[index].motTypFie.aspects.availableValues
                        .map((typeCode) => ({
                            code: typeCode.code,
                            name: translator({
                                id: typeCode.name,
                                defaultMessage: typeCode.name
                            })
                        }))
                    : [];

                const value = (item && item[path]) ? item[path] : availableValues.filter((availableValue) => availableValue.code === 'Gas')[0];

                return (
                    <DropdownSelectField
                        id={index + path}
                        className={styles.coverageForm}
                        onValueChange={(val, changedPath) => {
                            item.motTypFie = val;
                            handleValueChange(val, changedPath, index);
                        }}
                        dataType="string"
                        path={path}
                        value={value}
                        availableValues={availableValues}
                        searchable
                    />
                );
            }

            if (path === 'horFie') {
                const value = item.horFie ? item.horFie : '';

                return (
                    <InputNumberField
                        id={index + path}
                        className={styles.tableColumn}
                        path={path}
                        dataType="number"
                        onValueChange={(val, changedPath) => {
                            item.horFie = val;
                            handleValueChange(val, changedPath, index);
                        }}
                        value={value}
                    />
                );
            }

            if (path === 'serNumFie') {
                const value = item.serNumFie ? item.serNumFie : '';

                return (
                    <InputField
                        id={index + path}
                        className={styles.tableColumn}
                        path={path}
                        dataType="String"
                        onValueChange={(val, changedPath) => {
                            item.serNumFie = val;
                            handleValueChange(val, changedPath, index);
                        }}
                        value={value}
                    />
                );
            }

            if (path === 'curMarValFie') {
                const value = item.curMarValFie ? item.curMarValFie : '';

                return (
                    <InputField
                        id={index + path}
                        className={styles.tableColumn}
                        path={path}
                        dataType="number"
                        onValueChange={(val, changedPath) => {
                            const filteredVal = String(val).replace(/[^0-9]/g, ''); // keep only digits

                            item.curMarValFie = filteredVal;
                            handleValueChange(filteredVal, changedPath, index);
                        }}
                        value={value}
                    />
                );
            }

        }, [submissionVM.value, AuxiliaryMotorsVM.value, AuxiliaryMotorsVM.length, AuxiliaryMotorsVM.children, handleValueChange, translator])

    const removeAssignmentTableRow = useCallback(
        (item, index) => (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            (<Link
                onClick={(e) => removeAssignmentItem(e, index, item)}
                className={classNames(styles.deleteSection, styles.deleteRow)}                
            >
                <Icon icon="mi-delete" className={styles.deleteIcon} />
            </Link>)
        ),
        [removeAssignmentItem, translator]
    );

     const renderAddButton = useCallback(() => (
              <Button type='button' id='addAuxiliaryMotorsButtonId' className={styles.gwPrimaryButton} aria-label ={translator(messages.addAuxMotor)} onClick={(e) => addAuxiliaryMotors(e)}>
                      {translator(messages.add)}
                    </Button>
            ), [addAuxiliaryMotors, translator]);

    const overrideProps = useMemo(() => ({
        '@field': {
            showOptional: false,
            showRequired: true
        },
        [`AuxiliaryMotorsTable`]: {
            data: AuxiliaryMotorsVM.value,
        },
         'addAuxiliaryMotorsbtnDiv': {
            content: renderAddButton()
        }

    }), [AuxiliaryMotorsVM.value, renderAddButton])

    const resolvers = {
        resolveCallbackMap: {
            addAuxiliaryMotors,
            getTableCell,
            removeAssignmentTableRow
        }
    };

    const readValue = useCallback(
        (fieldId, fieldPath) => readViewModelValue(
            metadata.pageContent,
            AuxiliaryMotorsVM,
            fieldId,
            fieldPath,
            overrideProps
        ),
        [AuxiliaryMotorsVM, overrideProps]
    );

    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            model={AuxiliaryMotorsVM}
            overrideProps={overrideProps}
            resolveValue={readValue}
            onValueChange={handleValueChange}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );


}

AuxiliaryMotorsComponent.propTypes = wizardProps;
export default withRouter(AuxiliaryMotorsComponent);
